import { useMutation, useQuery } from "@tanstack/react-query";
import { ListOrdered } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";

interface MigrationReport {
  dryRun: boolean;
  vehicles: number;
  vehiclesToChange: number;
  inspectionsMarked: number;
  inspectionsRenumbered: number;
  conflicts: Array<{ vehicleId: string; problems: string[] }>;
  renumbered: Array<{ vehicleId: string; id: string; code: string; companyId: string; from: number; to: number }>;
}

/**
 * Administradores: corrige a numeração (Nª vistoria) das vistorias já gravadas para valer por veículo dentro de cada empresa.
 * Primeiro simula e mostra o que mudaria; só grava depois da confirmação.
 */
export function SequenceMigrationPanel() {
  const { user } = useAuth();
  const [report, setReport] = useState<MigrationReport | null>(null);
  const [confirming, setConfirming] = useState(false);
  const companies = useQuery({ queryKey: ["companies", "sequence-migration"], queryFn: () => api<{ items: Array<{ id: string; tradeName?: string; legalName?: string }> }>("/companies"), enabled: user?.role === "admin" });
  const companyName = (id: string) => { const company = companies.data?.items.find((item) => item.id === id); return company?.tradeName || company?.legalName || id; };
  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<MigrationReport>("/admin/sequence-migration", { method: "POST", body: JSON.stringify({ dryRun }) }),
    onSuccess: (result) => { setReport(result); setConfirming(false); }
  });
  if (user?.role !== "admin") return null;
  const pending = report?.dryRun && report.inspectionsMarked > 0;
  return <section className="card sequence-migration">
    <h2><ListOrdered aria-hidden="true"/> Numeração das vistorias por empresa</h2>
    <p>A "Nª vistoria" passa a contar cada veículo separadamente em cada empresa. Vistorias antigas mantêm a numeração anterior até esta correção. Primeiro simule: nada é gravado até você confirmar.</p>
    <div className="sequence-migration-actions">
      <button type="button" className="button secondary" onClick={() => run.mutate(true)} disabled={run.isPending}>{run.isPending && !confirming ? "Simulando…" : "Simular correção"}</button>
      {pending && !confirming && <button type="button" className="button primary" onClick={() => setConfirming(true)} disabled={run.isPending}>Aplicar correção</button>}
      {confirming && <>
        <span>Confirma gravar a nova numeração em {report?.inspectionsMarked} vistoria(s)? O número anterior fica guardado em cada uma.</span>
        <button type="button" className="button primary" onClick={() => run.mutate(false)} disabled={run.isPending}>{run.isPending ? "Aplicando…" : "Sim, aplicar"}</button>
        <button type="button" className="button ghost" onClick={() => setConfirming(false)} disabled={run.isPending}>Cancelar</button>
      </>}
    </div>
    {run.error && <div className="alert error">{(run.error as Error).message}</div>}
    {report && <div className="sequence-migration-report">
      <div className={`alert ${report.dryRun ? "" : "success"}`} role="status">
        {report.dryRun ? "Simulação (nada foi gravado). " : "Correção aplicada. "}
        {report.vehicles} veículo(s) analisado(s); {report.vehiclesToChange} com vistorias a ajustar; {report.inspectionsRenumbered} vistoria(s) mudam de número.
      </div>
      {report.renumbered.length > 0 && <div className="table-scroll"><table>
        <thead><tr><th>Vistoria</th><th>Empresa</th><th>Antes</th><th>Depois</th></tr></thead>
        <tbody>{report.renumbered.map((row) => <tr key={row.id}><td>{row.code || row.id}</td><td>{companyName(row.companyId)}</td><td>{row.from}ª</td><td><strong>{row.to}ª</strong></td></tr>)}</tbody>
      </table></div>}
      {report.conflicts.length > 0 && <div className="alert error">
        <strong>Veículos pulados (revisão manual):</strong>
        <ul>{report.conflicts.map((item) => <li key={item.vehicleId}>{item.vehicleId}: {item.problems.join(" ")}</li>)}</ul>
      </div>}
    </div>}
  </section>;
}
